const Konva = require('konva');

const MEASURE_NAME = 'measure-overlay';

const FIGMA_LINE = '#ff2920';
const FIGMA_LABEL_BG = 'rgba(24, 24, 27, 0.92)';
const FIGMA_LABEL_TEXT = '#fafafa';


export function clearMeasureOverlays(layer) {
  layer.find(`.${MEASURE_NAME}`).forEach((n) => n.destroy());
  layer.batchDraw();
}

/**
 * @param {import('konva').Layer} layer
 * @param {import('konva').Stage} stage
 * @param {import('./spacingMeasurements').SpacingSegment[]} segments
 * @param {import('./spacingMeasurements').ClientRect | null | undefined} hoverOutlineRect 
 */
export function drawMeasureOverlays(layer, stage, segments, hoverOutlineRect = null) {
  clearMeasureOverlays(layer);
  const hasSeg = segments?.length > 0;
  if (!hasSeg && !hoverOutlineRect) return;

  const scale = stage.scaleX() || 1;
  const inv = 1 / scale;
  const strokeW = Math.max(2.25 * inv, 1);
  const outlineW = Math.max(1.75 * inv, 0.85);
  const fontSize = Math.max(10 * inv, 8);
  const tickLen = 5 * inv;
  const padX = 5 * inv;
  const padY = 2 * inv;
  const radius = 3 * inv;

  if (hasSeg) {
    for (const s of segments) {
      const dx = s.x2 - s.x1;
      const dy = s.y2 - s.y1;
      const len = Math.hypot(dx, dy);
      if (len < 1e-6) continue;

      const main = new Konva.Line({
        points: [0, 0, dx, dy],
        stroke: FIGMA_LINE,
        strokeWidth: strokeW,
        name: MEASURE_NAME,
        listening: false,
        lineCap: 'round',
      });
      main.absolutePosition({ x: s.x1, y: s.y1 });
      layer.add(main);

      const nx = (-dy / len) * tickLen;
      const ny = (dx / len) * tickLen;
      const cap1 = new Konva.Line({
        points: [-nx / 2, -ny / 2, nx / 2, ny / 2],
        stroke: FIGMA_LINE,
        strokeWidth: strokeW,
        name: MEASURE_NAME,
        listening: false,
        lineCap: 'round',
      });
      cap1.absolutePosition({ x: s.x1, y: s.y1 });
      layer.add(cap1);

      const cap2 = new Konva.Line({
        points: [-nx / 2, -ny / 2, nx / 2, ny / 2],
        stroke: FIGMA_LINE,
        strokeWidth: strokeW,
        name: MEASURE_NAME,
        listening: false,
        lineCap: 'round',
      });
      cap2.absolutePosition({ x: s.x2, y: s.y2 });
      layer.add(cap2);

      const cx = (s.x1 + s.x2) / 2;
      const cy = (s.y1 + s.y2) / 2;
      const labelOff = 14 * inv;
      const tx = s.axis === 'H' ? cx : cx + labelOff;
      const ty = s.axis === 'H' ? cy - labelOff : cy;

      const labelText = `${Math.round(s.distance)}\u00A0px`;
      const text = new Konva.Text({
        text: labelText,
        fontSize,
        fontFamily: 'Inter, Roboto, Helvetica, Arial, sans-serif',
        fill: FIGMA_LABEL_TEXT,
        name: MEASURE_NAME,
        listening: false,
        align: 'left',
        verticalAlign: 'top',
      });

      const tw = text.width();
      const th = text.height();
      const bg = new Konva.Rect({
        width: tw + padX * 2,
        height: th + padY * 2,
        fill: FIGMA_LABEL_BG,
        cornerRadius: radius,
        name: MEASURE_NAME,
        listening: false,
      });

      const labelGroup = new Konva.Group({
        name: MEASURE_NAME,
        listening: false,
      });
      labelGroup.add(bg);
      text.position({ x: padX, y: padY });
      labelGroup.add(text);

      labelGroup.offsetX((tw + padX * 2) / 2);
      labelGroup.offsetY((th + padY * 2) / 2);
      labelGroup.absolutePosition({ x: tx, y: ty });
      layer.add(labelGroup);
    }
  }

  if (hoverOutlineRect && hoverOutlineRect.width > 0 && hoverOutlineRect.height > 0) {
    const outline = new Konva.Rect({
      x: hoverOutlineRect.x,
      y: hoverOutlineRect.y,
      width: hoverOutlineRect.width,
      height: hoverOutlineRect.height,
      stroke: FIGMA_LINE,
      strokeWidth: outlineW,
      fill: 'rgba(255, 114, 98, 0.06)',
      name: MEASURE_NAME,
      listening: false,
    });
    layer.add(outline);
  }

  layer.find(`.${MEASURE_NAME}`).forEach((n) => n.moveToTop());
  layer.batchDraw();
}
